/*
 * ScentBloom motion layer
 * - Lenis smooth scroll, driven by the GSAP ticker so ScrollTrigger stays in sync
 * - GSAP ScrollTrigger reveals + SplitText heading reveals
 * - Small interactive effects modelled on React Bits patterns (Magnet, SpotlightCard,
 *   GlareHover, ShinyText) re-implemented in vanilla JS for this Liquid theme.
 *
 * Injected after page load, desktop only, by snippets/scentbloom-motion.liquid (after gsap.min.js,
 * gsap-scrolltrigger.min.js, gsap-splittext.min.js and lenis.min.js). Content is never hidden
 * before this runs: only elements still below the fold are hidden here, right before they reveal.
 * The hero is left to its own CSS entrance so it never waits on JavaScript (it is the LCP element).
 */
(function () {
  const root = document.documentElement;
  const config = window.SBMotionConfig || {};

  if (!window.gsap || !window.ScrollTrigger) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const { gsap, ScrollTrigger, SplitText } = window;
  gsap.registerPlugin(ScrollTrigger);
  if (SplitText) gsap.registerPlugin(SplitText);

  const EASE = 'power3.out';
  const ACCENT = 'rgb(201 169 110 / 0.16)';

  /* ------------------------------------------------------------ Lenis */

  let lenis = null;

  function initLenis() {
    if (!config.smoothScroll || !window.Lenis || config.designMode) return;

    const headerHeight = () => parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;

    lenis = new window.Lenis({
      autoRaf: false,
      lerp: 0.1,
      anchors: { offset: -headerHeight() },
      autoToggle: true,
      stopInertiaOnNavigate: true,
      // Let drawers, dialogs, the chat window and other inner scroll areas scroll natively.
      prevent: (node) =>
        node instanceof Element &&
        node.matches(
          'dialog, [role="dialog"], .sb-chat__messages, predictive-search-component, .menu-drawer, [data-lenis-prevent]'
        ),
    });

    lenis.on('scroll', ScrollTrigger.update);
    gsap.ticker.add((time) => lenis.raf(time * 1000));
    gsap.ticker.lagSmoothing(0);

    // Horizon dialogs lock the page with html[scroll-lock]; mirror it on Lenis for browsers
    // that don't support the `transition-behavior: allow-discrete` trick autoToggle relies on.
    new MutationObserver(() => {
      if (root.hasAttribute('scroll-lock')) lenis.stop();
      else lenis.start();
    }).observe(root, { attributes: true, attributeFilter: ['scroll-lock'] });

    window.SBMotion.lenis = lenis;
  }

  /* ------------------------------------------------------------ Reveals */

  const REVEAL_ITEMS = [
    '.sb-collections__item',
    '.sb-steps__item',
    '.sb-reviews__item',
    '.sb-trust__item',
    '.sb-brands__item',
  ].join(',');

  const SPLIT_HEADINGS = [
    '.sb-collections__heading',
    '.sb-brands__heading',
    '.sb-reviews__heading',
    '.sb-trust__heading',
    '.sb-steps__heading',
    '.sb-blog__heading',
    '.sb-footer__newsletter-title',
  ].join(',');

  // Read every position first, then write, so the page is laid out once (no forced reflows).
  function belowFold(elements) {
    const fold = window.innerHeight;
    const tops = elements.map((el) => el.getBoundingClientRect().top);
    return elements.filter((_, i) => tops[i] > fold);
  }

  function revealItems(scope) {
    const items = belowFold(gsap.utils.toArray(scope.querySelectorAll(REVEAL_ITEMS)));
    if (!items.length) return;

    gsap.set(items, { autoAlpha: 0, y: 28 });
    ScrollTrigger.batch(items, {
      start: 'top 88%',
      once: true,
      onEnter: (batch) =>
        gsap.to(batch, {
          autoAlpha: 1,
          y: 0,
          duration: 0.9,
          ease: EASE,
          stagger: 0.08,
          overwrite: true,
          clearProps: 'transform',
        }),
    });
  }

  // Headings are only split when they are about to scroll into view, not all at start-up.
  function revealHeadings(scope) {
    const headings = belowFold(gsap.utils.toArray(scope.querySelectorAll(SPLIT_HEADINGS)));
    if (!headings.length) return;

    gsap.set(headings, { autoAlpha: 0 });
    headings.forEach((el) => {
      ScrollTrigger.create({
        trigger: el,
        start: 'top 95%',
        once: true,
        onEnter: () => {
          if (!SplitText) return gsap.to(el, { autoAlpha: 1, duration: 0.6 });
          SplitText.create(el, {
            type: 'lines',
            mask: 'lines',
            autoSplit: true,
            onSplit(self) {
              gsap.set(el, { autoAlpha: 1 });
              return gsap.from(self.lines, { yPercent: 105, duration: 1, ease: 'expo.out', stagger: 0.09 });
            },
          });
        },
      });
    });
  }

  // Gentle parallax on the hero image as the hero scrolls away (transform only, after load).
  function initHeroParallax(scope) {
    const hero = scope.querySelector('.sb-hero');
    const media = hero?.querySelector('.sb-hero__media');
    if (!media) return;

    gsap.to(media, {
      yPercent: 12,
      ease: 'none',
      scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: true },
    });
  }

  /* ------------------------------------------------------------ Interactive effects (React Bits-style) */

  // Magnet: pulls primary CTAs a few pixels towards the cursor.
  function initMagnets(scope) {
    scope.querySelectorAll('.sb-hero__btn--primary, .sb-steps__button, [data-sb-magnet]').forEach((el) => {
      if (el.dataset.sbMagnetReady) return;
      el.dataset.sbMagnetReady = 'true';
      el.classList.add('sb-magnet');

      const strength = 8;
      const padding = 40;
      const xTo = gsap.quickTo(el, '--sb-mx', { duration: 0.5, ease: 'power3.out' });
      const yTo = gsap.quickTo(el, '--sb-my', { duration: 0.5, ease: 'power3.out' });

      const onMove = (event) => {
        const rect = el.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const dx = event.clientX - cx;
        const dy = event.clientY - cy;
        const inRange = Math.abs(dx) < rect.width / 2 + padding && Math.abs(dy) < rect.height / 2 + padding;
        xTo(inRange ? gsap.utils.clamp(-strength, strength, dx / 6) : 0);
        yTo(inRange ? gsap.utils.clamp(-strength, strength, dy / 6) : 0);
      };

      window.addEventListener('pointermove', onMove, { passive: true });
    });
  }

  // SpotlightCard: a soft champagne glow that follows the pointer.
  function initSpotlights(scope) {
    scope.querySelectorAll('.sb-reviews__card, [data-sb-spotlight]').forEach((el) => {
      if (el.dataset.sbSpotlightReady) return;
      el.dataset.sbSpotlightReady = 'true';
      el.classList.add('sb-spotlight');
      el.style.setProperty('--sb-spot-color', ACCENT);

      el.addEventListener('pointermove', (event) => {
        const rect = el.getBoundingClientRect();
        el.style.setProperty('--sb-spot-x', `${event.clientX - rect.left}px`);
        el.style.setProperty('--sb-spot-y', `${event.clientY - rect.top}px`);
      });
    });
  }

  // GlareHover: a light sweep across collection images on hover.
  function initGlare(scope) {
    scope.querySelectorAll('.sb-collections__card, [data-sb-glare]').forEach((el) => {
      if (el.querySelector(':scope > .sb-glare')) return;
      const glare = document.createElement('span');
      glare.className = 'sb-glare';
      glare.setAttribute('aria-hidden', 'true');
      el.appendChild(glare);
    });
  }

  /* ------------------------------------------------------------ Boot */

  function initScope(scope) {
    if (config.scrollAnimations) {
      initHeroParallax(scope);
      revealHeadings(scope);
      revealItems(scope);
    }

    if (config.interactiveEffects) {
      initMagnets(scope);
      initSpotlights(scope);
      initGlare(scope);
    }
  }

  window.SBMotion = { lenis: null, refresh: () => ScrollTrigger.refresh() };

  root.classList.add('sb-motion');
  initLenis();

  const ctx = gsap.context(() => initScope(document));
  window.SBMotion.ready = true;

  // Recalculate trigger positions once fonts have settled.
  document.fonts?.ready.then(() => ScrollTrigger.refresh());

  // Theme editor: re-run for sections that are re-rendered.
  if (config.designMode) {
    document.addEventListener('shopify:section:load', (event) => {
      ctx.add(() => initScope(event.target));
      ScrollTrigger.refresh();
    });
  }
})();
